/**
 * The `auth.organisationuser."RoleKey"` value that entitles a RiskSmart-internal
 * user to the support bypass (COI-819).
 *
 * Mirrors the data-layer's `SUPPORT_MEMBERSHIP_ROLE`. Used here only to READ the
 * current state — the tRPC procedure takes a boolean, so the web layer never
 * sends a role key.
 */
export const SUPPORT_MEMBERSHIP_ROLE = 'CustomerSupport';

/**
 * What a target user's support entitlement looks like, as the UI needs to say
 * it.
 *
 * Two independent facts produce four states, and collapsing them to a boolean is
 * what made the original bug invisible: the DB's rule is
 * `RoleKey = 'CustomerSupport'` **AND** `auth.user."IsCustomerSupport"`, so a
 * membership can carry the role and confer nothing.
 *
 * - `granted`   — both factors. The bypass is live.
 * - `available` — a RiskSmart-internal user whose membership does not (yet) say
 *                 support. Granting is a one-switch operation.
 * - `inert`     — the membership says `CustomerSupport` but the user has no
 *                 RiskSmart identity, so the rule derives nothing. This is the
 *                 population that carried the V2 support role into V3 and then
 *                 quietly stopped being provisioned: before COI-819 the loose
 *                 one-factor INSERT arm granted them the bypass, and the
 *                 1786695146000 backfill takes it back. Nothing in the UI said
 *                 so, which is the confusion this state exists to end.
 * - `none`      — an ordinary member. Nothing to show.
 */
export type SupportAccessStatus = 'available' | 'granted' | 'inert' | 'none';

export const getSupportAccessStatus = ({
  targetIsCustomerSupport,
  membershipRoleKey,
}: {
  targetIsCustomerSupport: boolean | null | undefined;
  membershipRoleKey: string | null | undefined;
}): SupportAccessStatus => {
  const holdsSupportRole = membershipRoleKey === SUPPORT_MEMBERSHIP_ROLE;

  if (targetIsCustomerSupport) {
    return holdsSupportRole ? 'granted' : 'available';
  }

  return holdsSupportRole ? 'inert' : 'none';
};

/**
 * Whether the support-entitlement field renders at all.
 *
 * Three conditions:
 *
 * - **V3 only.** On V2 the membership role IS the user's role and the
 *   single-role dropdown owns that column, so a second control writing it would
 *   fight the first.
 * - **RiskSmart-internal caller.** Support access is RiskSmart's own
 *   entitlement, and the four states it distinguishes (`inert` in particular)
 *   only mean anything to someone who administers it. A customer admin reads it
 *   as an unexplained extra field they cannot act on, so it is not shown to
 *   them at all. This reverses the COI-819 decision to make it customer-visible.
 *   `useIsCustomerSupport` is the caller's CS-ness resolved server-side from
 *   `auth.user_role`, the same source `assertCustomerSupport` gates the write
 *   with.
 * - **A target with something to say.** `none` is an ordinary member.
 *
 * Hiding is presentation only — `canEditSupportAccess` below still gates the
 * switch, and the tRPC procedure and the data-layer each re-check the caller
 * independently.
 */
export const shouldShowSupportAccess = ({
  isMultiRoleEnabled,
  callerIsCustomerSupport,
  status,
}: {
  isMultiRoleEnabled: boolean;
  callerIsCustomerSupport: boolean;
  status: SupportAccessStatus;
}): boolean =>
  isMultiRoleEnabled && callerIsCustomerSupport && status !== 'none';

/**
 * Whether the field is editable rather than read-only.
 *
 * Two conditions, both necessary:
 *
 * - **CustomerSupport caller.** `settings_users:update` is held by every customer
 *   SettingsManager, and support access is the perm_v2 RLS bypass — the same
 *   escalation route `1785258221000` sealed on the custom-role side, so it must
 *   not be reachable from an ordinary admin permission. On V3 the caller's
 *   CS-ness comes from `auth.user_role` through
 *   `role.isCurrentUserCustomerSupport`, never from a JWT claim.
 * - **A target the write can actually serve.** `inert` and `none` targets have no
 *   RiskSmart identity, and the DB's two-factor rule refuses to derive
 *   `CustomerSuccess` without one — the switch would promise what the write
 *   cannot deliver. `inert` still RENDERS, because saying "set, but not in
 *   effect" is the whole point; it just cannot be toggled.
 */
export const canEditSupportAccess = ({
  callerIsCustomerSupport,
  status,
}: {
  callerIsCustomerSupport: boolean;
  status: SupportAccessStatus;
}): boolean =>
  callerIsCustomerSupport && (status === 'granted' || status === 'available');
